import { useState, useEffect } from 'react';
import { authFetch } from '@/utils/authFetch';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Calendar as CalendarIcon, Clock, Users as UsersIcon, Video, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Calendar() {
  const [meetings, setMeetings] = useState([]);
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    meeting_date: new Date().toISOString().split('T')[0],
    meeting_time: '10:00',
    duration: '60',
    meeting_link: '',
    attendees: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [meetingsRes, usersRes, userRes] = await Promise.all([
        authFetch(`${BACKEND_URL}/api/meetings`),
        authFetch(`${BACKEND_URL}/api/users`),
        authFetch(`${BACKEND_URL}/api/auth/me`)
      ]);

      if (meetingsRes.ok) setMeetings(await meetingsRes.json());
      if (usersRes.ok) setUsers(await usersRes.json());
      if (userRes.ok) setCurrentUser(await userRes.json());
    } catch (error) {
      toast.error('Failed to load meetings');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await authFetch(`${BACKEND_URL}/api/meetings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Meeting scheduled successfully');
        setDialogOpen(false);
        setFormData({ title: '', description: '', meeting_date: new Date().toISOString().split('T')[0], meeting_time: '10:00', duration: '60', meeting_link: '', attendees: [] });
        fetchData();
      } else {
        toast.error('Failed to schedule meeting');
      }
    } catch (error) {
      toast.error('Failed to schedule meeting');
    }
  };

  const handleDelete = async (meetingId) => {
    if (!window.confirm('Delete this meeting?')) return;
    try {
      const response = await authFetch(`${BACKEND_URL}/api/meetings/${meetingId}`, { method: 'DELETE' });
      if (response.ok) {
        toast.success('Meeting deleted');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to delete meeting');
    }
  };

  if (loading) return <Layout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div></Layout>;

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Calendar & Meetings</h1>
            <p className="text-slate-600 mt-1">Schedule and manage team meetings</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-600 hover:bg-indigo-700">
                <Plus className="w-4 h-4 mr-2" />
                Schedule Meeting
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Schedule Meeting</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div><Label>Meeting Title *</Label><Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required /></div>
                <div><Label>Description</Label><Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={2} /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Date *</Label><Input type="date" value={formData.meeting_date} onChange={(e) => setFormData({...formData, meeting_date: e.target.value})} required /></div>
                  <div><Label>Time *</Label><Input type="time" value={formData.meeting_time} onChange={(e) => setFormData({...formData, meeting_time: e.target.value})} required /></div>
                </div>
                <div><Label>Duration (minutes)</Label><Input type="number" value={formData.duration} onChange={(e) => setFormData({...formData, duration: e.target.value})} /></div>
                <div><Label>Meeting Link</Label><Input value={formData.meeting_link} onChange={(e) => setFormData({...formData, meeting_link: e.target.value})} placeholder="https://meet.google.com/..." /></div>
                <div className="flex justify-end space-x-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit">Schedule</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {meetings.map((meeting) => (
            <Card key={meeting.meeting_id} className="card-hover">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-base">{meeting.title}</CardTitle>
                    {meeting.description && <p className="text-sm text-slate-500 mt-1">{meeting.description}</p>}
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(meeting.meeting_id)} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center text-sm text-slate-600"><CalendarIcon className="w-4 h-4 mr-2 text-slate-400" />{meeting.meeting_date}</div>
                <div className="flex items-center text-sm text-slate-600"><Clock className="w-4 h-4 mr-2 text-slate-400" />{meeting.meeting_time} {meeting.duration && `(${meeting.duration} min)`}</div>
                {meeting.attendees && meeting.attendees.length > 0 && <div className="flex items-center text-sm text-slate-600"><UsersIcon className="w-4 h-4 mr-2 text-slate-400" />{meeting.attendees.length} attendees</div>}
                {meeting.meeting_link && <div className="flex items-center text-sm"><Video className="w-4 h-4 mr-2 text-slate-400" /><a href={meeting.meeting_link} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">Join Meeting</a></div>}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </Layout>
  );
}